import React from 'react';
import { useLabs } from '../hooks/useLabs';
import './HomeView.css';

export default function HomeView({ onNavigate }) {
  const { routes, setSelectedRoute } = useLabs();

  const handleStartLabs = (routeId) => {
    if (routeId) setSelectedRoute(routeId);
    onNavigate?.('labs');
  };

  return (
    <div className="home-view">
      {/* Sección Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              🔒 Security Analyzer
            </h1>
            <p className="hero-subtitle">
              La plataforma completa para aprender ciberseguridad a través de laboratorios interactivos, análisis de URLs y herramientas profesionales
            </p>
            <div className="hero-actions">
              <button 
                className="btn-primary btn-large"
                onClick={() => handleStartLabs(routes?.[0]?.id)}
              >
                🚀 Comenzar Laboratorios
              </button>
              <button 
                className="btn-secondary btn-large"
                onClick={() => onNavigate?.('basic')}
              >
                🔍 Analizar URL
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="animated-gradient"></div>
            <div className="floating-card card-1">
              <span>🛡️</span>
              <p>SSL Seguro</p>
            </div>
            <div className="floating-card card-2">
              <span>🔑</span>
              <p>Análisis Avanzado</p>
            </div>
            <div className="floating-card card-3">
              <span>📊</span>
              <p>Reportes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Estadísticas Rápidas */}
      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-icon">📚</span>
            <div className="stat-content">
              <h3>{routes?.length || 0}</h3>
              <p>Rutas de Aprendizaje</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">🎯</span>
            <div className="stat-content">
              <h3>{routes?.reduce((acc, r) => acc + (r.niveles?.length || 0), 0) || 0}</h3>
              <p>Desafíos Disponibles</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💪</span>
            <div className="stat-content">
              <h3>100%</h3>
              <p>Práctico</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">🏆</span>
            <div className="stat-content">
              <h3>∞</h3>
              <p>Sin Límite</p>
            </div>
          </div>
        </div>
      </section>

      {/* Características Principales */}
      <section className="features-section">
        <h2 className="section-title">Herramientas Disponibles</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🔍</div>
            <h3>Análisis de URLs</h3>
            <p>Evalúa seguridad de cabeceras, SSL/TLS, CORS, contenido y detecta vulnerabilidades comunes.</p>
            <button className="btn-small" onClick={() => onNavigate?.('basic')}>
              Analizar
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon">✉️</div>
            <h3>Email Analyzer</h3>
            <p>Detecta direcciones de correo inválidas, dominios desechables y ataques de typosquatting.</p>
            <button className="btn-small" onClick={() => onNavigate?.('email')}>
              Verificar
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🗂️</div>
            <h3>Enumeración</h3>
            <p>Descubre rutas, subdominios y endpoints ocultos en aplicaciones web objetivo.</p>
            <button className="btn-small" onClick={() => onNavigate?.('enum')}>
              Enumerar
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🧪</div>
            <h3>Labs Interactivos</h3>
            <p>Escenarios de penetration testing con máquinas virtuales simuladas y desafíos progresivos.</p>
            <button className="btn-small" onClick={() => handleStartLabs(routes?.[0]?.id)}>
              Entrar Labs
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📈</div>
            <h3>Reportes</h3>
            <p>Genera reportes profesionales de seguridad listos para presentar a clientes.</p>
            <button className="btn-small" onClick={() => onNavigate?.('basic')}>
              Ver Reportes
            </button>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎓</div>
            <h3>Certificaciones</h3>
            <p>Obtén certificados de competencia al completar todas las rutas de aprendizaje.</p>
            <button className="btn-small" onClick={() => handleStartLabs(routes?.[0]?.id)}>
              Mis Certificados
            </button>
          </div>
        </div>
      </section>

      {/* Rutas de Labs */}
      {routes?.length > 0 && (
        <section className="labs-section">
          <h2 className="section-title">Comienza tu Viaje de Aprendizaje</h2>
          <div className="labs-grid">
            {routes.map((route, index) => (
              <div key={route.id} className={`lab-card lab-card-${index}`}>
                <div className="lab-header">
                  <span className="lab-icon">{route.icono}</span>
                  <span className={`difficulty-badge difficulty-${String(route.dificultad || 'Intermedio').toLowerCase()}`}>
                    {route.dificultad}
                  </span>
                </div>
                <h3 className="lab-title">{route.titulo}</h3>
                <p className="lab-description">{route.descripcion}</p>
                <div className="lab-meta">
                  <span className="meta-item">
                    <span className="meta-icon">📊</span>
                    {route.niveles?.length || 0} niveles
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">⏱️</span>
                    {route.duracion_estimada_horas}h
                  </span>
                </div>
                <button 
                  className="lab-button"
                  onClick={() => handleStartLabs(route.id)}
                >
                  Comenzar →
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>¿Listo para mejorar tu seguridad?</h2>
          <p>Únete a miles de profesionales que confían en Security Analyzer para entrenar sus habilidades</p>
          <button 
            className="btn-primary btn-large"
            onClick={() => handleStartLabs(routes?.[0]?.id)}
          >
            Comenzar Ahora Gratis 🚀
          </button>
        </div>
      </section>
    </div>
  );
}
